import { useMemo, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Button as Button } from '@factorialco/f0-react'
import { Spinner } from '@factorialco/f0-react/dist/experimental'
import { Plus } from '@factorialco/f0-react/icons/app'

import i18n from 'modules/core/lib/i18n'

import { ApprovalSettingsModal } from './ApprovalSettingsModal'
import { ApproverSelection } from './ApproverSelection'
import {
  StrategyItem,
  StrategySelectorEmployee,
  StrategySelectorLegacyRole,
  StrategySelectorTeam,
  StrategySelectorTreeRoles,
  StrategyType,
} from './utils/interfaces'
import { getStrategyDisplayInfo } from './utils/strategyDisplayInfoUtils'
import { getStrategyOptions } from './utils/strategyUtils'

interface ApproverSelectionContainerProps {
  strategies: StrategyItem[]
  unselectableStrategies?: StrategyType[]
  onAddStrategies: (strategies: StrategyItem[]) => void
  onDeleteStrategy: (strategyId: string) => void
  employees?: Array<StrategySelectorEmployee>
  unselectableEmployees?: Array<number>
  teams?: Array<StrategySelectorTeam>
  unselectableTeams?: Array<number>
  legacyRoles?: Array<StrategySelectorLegacyRole>
  treeRoles?: Array<StrategySelectorTreeRoles>
  unselectableRoles?: Array<number | string>
  isLoading: boolean
  entityName: string
  maxStrategies?: number
}

export const ApproverSelectionContainer = ({
  strategies,
  unselectableStrategies = [],
  onAddStrategies,
  onDeleteStrategy,
  employees = [],
  unselectableEmployees = [],
  teams = [],
  unselectableTeams = [],
  legacyRoles = [],
  treeRoles = [],
  unselectableRoles = [],
  isLoading,
  entityName,
  maxStrategies = Number.POSITIVE_INFINITY,
}: ApproverSelectionContainerProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false)

  // All strategies, except none strategies already used
  const availableStrategies = useMemo(() => {
    return Object.values(getStrategyOptions())
      .filter((value) => {
        if (unselectableStrategies.includes(value.value)) return false
        if (value.ui_type !== 'none') return true
        return !strategies.some((s) => s.type === value.value)
      })
      .map((value) => value.value)
  }, [strategies, unselectableStrategies])

  const joinRoles = useMemo(
    () =>
      [...legacyRoles, ...treeRoles].map((role) => ({
        id: role.id,
        name: role.name,
        uuid: 'uuid' in role ? role.uuid : role.id.toString(),
        ancestor: null,
      })),
    [legacyRoles, treeRoles]
  )

  const strategiesDisplay = useMemo(() => {
    return strategies.map((strategy) => {
      const displayInfo = getStrategyDisplayInfo({
        strategy,
        employees,
        teams,
        roles: joinRoles,
      })

      return (
        <ApproverSelection
          key={strategy.id}
          id={strategy.id}
          onDelete={onDeleteStrategy}
          label={displayInfo.label}
          avatarUrl={displayInfo.avatarSrc}
          avatarText={displayInfo.avatarText}
        />
      )
    })
  }, [strategies, employees, teams, joinRoles, onDeleteStrategy])

  const availableEmployees = useMemo(
    () => employees.filter((employee) => !unselectableEmployees.includes(employee.id)),
    [employees, unselectableEmployees]
  )
  const availableTeams = useMemo(
    () => teams.filter((team) => !unselectableTeams.includes(team.id)),
    [teams, unselectableTeams]
  )
  const availableLegacyRoles = useMemo(
    () => legacyRoles.filter((role) => !unselectableRoles.includes(role.id)),
    [legacyRoles, unselectableRoles]
  )

  const availableTreeRoles = useMemo(
    () => treeRoles.filter((role) => !unselectableRoles.includes(role.uuid)),
    [treeRoles, unselectableRoles]
  )

  return (
    <Box gap="s8" width="full">
      {strategies.length > 0 && (
        <Box gap="s2" width="full">
          {isLoading ? <Spinner /> : strategiesDisplay}
        </Box>
      )}

      {strategies.length < maxStrategies && (
        <Box flexDirection="row" justifyContent="flexStart" width="full" paddingTop="s8">
          <Button
            icon={Plus}
            label={i18n.t('workflows.sidepanel.approval.add_approvers.title_with_entity', {
              entityName,
            })}
            onClick={() => setIsModalOpen(true)}
            size="md"
            variant="outline"
          />
        </Box>
      )}

      <ApprovalSettingsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onAddStrategies={onAddStrategies}
        availableEmployees={availableEmployees}
        availableTeams={availableTeams}
        availableLegacyRoles={availableLegacyRoles}
        availableTreeRoles={availableTreeRoles}
        entityName={entityName}
        isLoading={isLoading}
        availableStrategies={availableStrategies}
      />
    </Box>
  )
}
